import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { setAppLang } from "@/i18n/appLang";
import { Copy, Check, Download, Smartphone, ShieldCheck, Landmark, Phone, Mail, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
const beDate = (d) => (d ? d.split("-").reverse().join("/") : "");

const CopyRow = ({ label, value, testId }) => {
  const [ok, setOk] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); } catch { /* noop */ }
    setOk(true); toast.success(`${label} copié`); setTimeout(() => setOk(false), 1800);
  };
  return (
    <button type="button" onClick={copy} data-testid={testId} className="flex w-full items-center justify-between gap-3 rounded-2xl border border-[#E2E7F7] bg-white px-4 py-3 text-left transition-colors hover:border-[#3A5CC8]">
      <span>
        <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-[#93A0BC]">{label}</span>
        <span className="block font-mono text-sm font-semibold text-[#0F2645] break-all">{value}</span>
      </span>
      {ok ? <Check className="h-4 w-4 shrink-0 text-emerald-600" /> : <Copy className="h-4 w-4 shrink-0 text-[#3A5CC8]" />}
    </button>
  );
};

export default function PublicPay() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [starting, setStarting] = useState(false);
  const [checking, setChecking] = useState(params.get("retour") === "1");

  const load = () => axios.get(`${API}/facture/${token}/paiement`).then(({ data }) => { setAppLang(data.lang); setData(data); setDeclared(Boolean(data.declared_at)); }).catch(() => setError(true));

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!checking) return undefined;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data: s } = await axios.get(`${API}/facture/${token}/paiement/mollie/statut`);
        if (s.paid) { setChecking(false); await load(); toast.success("Paiement reçu — merci !"); return; }
        if (["failed", "canceled", "expired"].includes(s.status) || tries >= 6) { setChecking(false); if (s.status) toast.error("Le paiement n'a pas abouti. Vous pouvez réessayer."); return; }
      } catch { /* retry */ }
      setTimeout(tick, 2500);
    };
    tick();
    return undefined;
  }, [checking, token]);

  const payOnline = async () => {
    setStarting(true);
    try {
      const { data: r } = await axios.post(`${API}/facture/${token}/paiement/mollie`);
      window.location.assign(r.checkout_url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Paiement en ligne indisponible pour le moment."); setStarting(false); }
  };

  const copyAll = async () => {
    const txt = `Bénéficiaire : ${data.company}\nIBAN : ${data.iban}\nMontant : ${money(data.amount_due, data.currency)}\nCommunication : ${data.communication}`;
    try { await navigator.clipboard.writeText(txt); } catch { /* noop */ }
    toast.success("Données du virement copiées — collez-les dans votre app bancaire");
  };

  const declare = async () => {
    try {
      await axios.post(`${API}/facture/${token}/paiement/declare`);
      setDeclared(true); toast.success("Merci ! L'entreprise est prévenue de votre paiement.");
    } catch { toast.error("Impossible d'enregistrer, réessayez."); }
  };

  if (error) return <div className="min-h-screen bg-[#F3F5FC] p-8 text-center text-[#5C6A87]" data-testid="pay-error">Lien de paiement introuvable.</div>;
  if (!data) return <div className="min-h-screen bg-[#F3F5FC]" />;
  const accent = data.doc_color || "#3A5CC8";

  return (
    <div className="font-body min-h-screen bg-[#F3F5FC] text-[#0F2645]">
      <main data-testid="public-pay" className="mx-auto max-w-md px-5 py-8">
        <div className="text-center">
          {data.logo_media_id ? <img src={`${API}/media/${data.logo_media_id}`} alt={data.company} className="mx-auto h-14 w-auto max-w-[220px] object-contain" /> : null}
          <p data-testid="pay-company" className="mt-3 font-heading text-base font-bold text-[#0F2645] md:text-lg">{data.company}</p>
          <p className="text-xs text-[#5C6A87]">{data.kind_label} {data.reference} · {data.client_name} · échéance {beDate(data.due_date)}</p>
        </div>

        <div className="mt-6 rounded-3xl p-6 text-center text-white shadow-[0_30px_70px_-30px_rgba(8,16,77,0.6)]" style={{ background: "#08104D" }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9FB4F2]">{data.paid ? "Facture réglée" : "Montant à payer"}</p>
          <p data-testid="pay-amount" className="mt-2 font-heading text-4xl font-extrabold tracking-tight">{money(data.paid ? data.total : data.amount_due, data.currency)}</p>
          {data.paid ? <p className="mt-2 text-sm text-white/70">Merci, cette facture est déjà payée.</p> : null}
        </div>

        {!data.paid && (data.iban || data.mollie) ? (
          <>
            {data.mollie ? (
              <section className="mt-6 rounded-3xl border-2 p-5 text-center" style={{ borderColor: accent }}>
                <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: accent }}><Zap className="h-3.5 w-3.5" /> Paiement instantané</p>
                <button type="button" onClick={payOnline} disabled={starting || checking} data-testid="pay-mollie" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-4 text-base font-bold text-white shadow-[0_6px_16px_-8px_rgba(58,92,200,0.5)] transition-transform hover:-translate-y-0.5 disabled:opacity-60" style={{ background: accent }}>
                  {starting || checking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Smartphone className="h-5 w-5" />}
                  {checking ? "Vérification du paiement…" : `Payer ${money(data.amount_due, data.currency)} avec mon app bancaire`}
                </button>
                <p className="mt-3 text-xs leading-relaxed text-[#5C6A87]">
                  {(data.mollie_methods || []).filter((m) => ["bancontact", "payconiq", "banktransfer", "kbc", "belfius", "creditcard", "applepay"].includes(m)).map((m) => ({ bancontact: "Bancontact", payconiq: "Payconiq", banktransfer: "Virement", kbc: "KBC/CBC", belfius: "Belfius", creditcard: "Carte", applepay: "Apple Pay" })[m]).join(" · ") || "Bancontact · Payconiq"} — votre app bancaire s'ouvre avec le virement pré-rempli ; la facture est marquée payée automatiquement.
                </p>
              </section>
            ) : null}
            {data.iban ? (
            <section className="mt-6 rounded-3xl border border-[#E2E7F7] bg-white p-5 text-center">
              <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: accent }}><Smartphone className="h-3.5 w-3.5" /> Scannez avec votre app bancaire</p>
              <img data-testid="pay-qr" src={`data:image/png;base64,${data.qr}`} alt="QR code de paiement SEPA" className="mx-auto mt-3 h-56 w-56 rounded-xl" />
              <p className="mt-3 text-xs leading-relaxed text-[#5C6A87]">KBC, Belfius, ING, BNP Paribas Fortis, Argenta, Crelan… : ouvrez votre app, choisissez « Scanner / Payer par QR » — le virement est pré-rempli (bénéficiaire, montant, communication).</p>
              <a href={`${API}/facture/${token}/paiement/qr.png`} download data-testid="pay-qr-download" className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#E2E7F7] px-4 py-2.5 text-xs font-semibold text-[#0F2645] hover:border-[#3A5CC8]">
                <Download className="h-3.5 w-3.5" /> Sur téléphone ? Enregistrer le QR puis l'importer dans l'app bancaire
              </a>
            </section>
            ) : null}

            {data.iban ? (
            <section className="mt-4 space-y-2">
              <p className="px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C6A87]">Ou par virement manuel</p>
              <CopyRow label="IBAN" value={data.iban} testId="pay-copy-iban" />
              <CopyRow label="Communication" value={data.communication} testId="pay-copy-communication" />
              <CopyRow label="Bénéficiaire" value={data.company} testId="pay-copy-name" />
              <button type="button" onClick={copyAll} data-testid="pay-copy-all" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5" style={{ background: accent }}>
                <Landmark className="h-4 w-4" /> Copier tout et ouvrir mon app bancaire
              </button>
            </section>
            ) : null}

            <section className="mt-6 rounded-3xl border border-[#E2E7F7] bg-white p-5">
              {declared ? (
                <p data-testid="pay-declared" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700"><ShieldCheck className="h-4 w-4" /> Paiement signalé — merci, l'entreprise a été prévenue.</p>
              ) : (
                <>
                  <p className="text-sm text-[#5C6A87]">Virement effectué ?</p>
                  <button type="button" onClick={declare} data-testid="pay-declare" className="mt-2 w-full rounded-2xl border border-[#08104D] px-5 py-3 text-sm font-bold text-[#0F2645] hover:bg-[#0F2645] hover:text-white">J'ai effectué le paiement</button>
                </>
              )}
            </section>
          </>
        ) : null}

        <p className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center text-xs text-[#93A0BC]">
          {data.phone ? <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {data.phone}</span> : null}
          {data.email ? <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {data.email}</span> : null}
        </p>
        <p className="mt-3 text-center text-[10px] text-[#A3ADC2]">Paiement sécurisé par virement SEPA vers le compte de l'entreprise · by Devisto</p>
      </main>
    </div>
  );
}
